export interface Reason {
  icon: string;
  title: string;
  desc: string;
}

interface Props {
  reasons: Reason[];
  image?: string;
  title?: string;
  bodyText?: string;
}

export default function SectionWhyChooseUs({ reasons, image, title = 'Why Clients Choose Our Firm', bodyText }: Props) {
  const displayImage = image ?? 'https://public.readdy.ai/ai/img_res/448b9d0f702786e2a81312674f1e1151.jpg';

  return (
    <section className="bg-[#050a12] py-24 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '30px 30px' }} />
      <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #c9a84c50, transparent)' }} />

      {/* Gold glow top-right */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] opacity-[0.04]" style={{ background: 'radial-gradient(circle, #c9a84c, transparent 70%)' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* Left content */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-[2px] bg-[#c9a84c]" />
              <span className="text-[#c9a84c] text-[11px] font-bold uppercase tracking-[0.28em]">Why Choose Us</span>
            </div>
            <h2
              className="text-white font-bold leading-[1.08] mb-5"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(30px, 4vw, 52px)' }}
            >
              {title}
            </h2>
            <div className="flex items-center gap-2.5 mb-7">
              <div className="w-12 h-[3px] bg-[#c9a84c] rounded-full" />
              <div className="w-3 h-[3px] bg-[#c9a84c]/35 rounded-full" />
            </div>
            {bodyText && <p className="text-white/55 text-lg leading-relaxed mb-9">{bodyText}</p>}

            {/* Mobile image */}
            <div className="lg:hidden rounded-2xl overflow-hidden h-60 mb-8 border border-white/8">
              <img src={displayImage} alt="Attorney Dave Bunger" className="w-full h-full object-cover object-top" />
            </div>

            {/* Reasons */}
            <div className="space-y-3">
              {reasons.map((r) => (
                <div
                  key={r.title}
                  className="group flex items-start gap-4 bg-white/3 border border-white/7 rounded-xl p-5 hover:bg-white/6 hover:border-[#c9a84c]/30 transition-all duration-300 cursor-default"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#c9a84c]/12 border border-[#c9a84c]/20 rounded-xl flex-shrink-0 group-hover:bg-[#c9a84c]/20 transition-colors duration-300">
                    <i className={`${r.icon} text-[#c9a84c] text-base`} />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-[15px] mb-1">{r.title}</h3>
                    <p className="text-white/45 text-sm leading-relaxed">{r.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right image panel (desktop) */}
          <div className="hidden lg:block">
            <div className="relative group">
              {/* Glow ring */}
              <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-[#c9a84c]/15 to-transparent opacity-60" />

              <div className="relative rounded-2xl overflow-hidden border border-white/10 group-hover:border-[#c9a84c]/25 transition-colors duration-500" style={{ height: '580px' }}>
                <img
                  src="https://storage.readdy-site.link/project_files/ded8308e-abc8-4fab-8e12-5ebe93ce4b5e/f90ca2e2-cf04-4af7-9f7c-024ebf83cacd_dave.jpeg?v=0b830deae816dbe4c28e116aa48bc238"
                  alt="Dave Bunger - Attorney at Bunger Law Firm"
                  className="w-full h-full object-cover object-top"
                />

                {/* Dark gradient overlay bottom */}
                <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#050a12] to-transparent" />

                {/* Floating review card */}
                <div className="absolute bottom-5 left-5 right-5 bg-[#050a12]/95 backdrop-blur-sm border border-white/10 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    {[1,2,3,4,5].map(i => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-[#c9a84c] text-xs" />
                      </div>
                    ))}
                    <span className="text-white/50 text-xs ml-1">5.0 · 50+ Reviews</span>
                  </div>
                  <p className="text-white font-semibold text-sm leading-snug mb-2">
                    "Dave fights for you like no other attorney. Truly exceptional results."
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-white/35 text-xs">— Verified Google Client</span>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-google-fill text-[#4285F4] text-xs" />
                      </div>
                      <span className="text-white/35 text-[11px]">Google Review</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Side stat badge */}
              <div className="absolute -right-4 top-16 bg-[#c9a84c] rounded-xl px-4 py-3 text-center">
                <p className="text-[#050a12] font-black text-2xl leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>10+</p>
                <p className="text-[#050a12]/70 text-[10px] font-bold uppercase tracking-wider">Years</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
